import { type GridSizeChangedEvent } from "ag-grid-community";
import { PureComponent, type ReactElement } from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";
import { Dialog, LoadingBar, LoadingSpinner, TriggerButton } from "widgets";

import { encodeFilter, type RsqlFilterConstraint } from "@/app-utils/rsql.ts";
import { type ISelectionModel } from "@/app-utils/selection-model.ts";
import { type IStringAnyMap } from "@/app-utils/type.ts";
import {
  type InputTableQuerySpec,
  type InputUserParamsInput,
  type SequenceFeatureTypes,
  type View,
} from "@/graphql.ts";
import { updateUserParams } from "@/redux/modules/auth.ts";
import { loadView, savedViewAppliesToTableQuerySpec } from "@/redux/modules/saved-view.ts";
import {
  clearTableDataSlice,
  createUniqueKeyFromTableSpec,
  destroyActiveTable,
  ensureRowIsVisible,
  fetchRowPosition,
  filterByTableColumn,
  getActiveExpansionPath,
  getTableDataKey,
  type ITableColumn,
  type ITableData,
  type ITableInitializationOptions,
  loadTable,
  loadTableColumns,
  loadTableRows,
  resetAllFiltersAndReloadTable,
  selectTableRange,
  selectTableRow,
  setColumnsVisibility,
  setOrderedColumnList,
  showGuidanceMessageForEntity,
  sortByMultipleTableColumns,
  sortByTableColumn,
  toggleSelection,
  updateTableGuidanceState,
} from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { COLUMN_MENU_UPWARDS_HEIGHT_THRESHOLD, getSequenceFeatureTypeDisplayName } from "@/settings/settings.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

import { COLUMN_SELECTION_BUTTON_PLACEHOLDER_ID, COLUMN_SELECTION_GUIDANCE_TIMER } from "../ColumnSelectionButton.js";
import { PopoverController } from "../overlays/components/OverlayControllers.js";
import { ModalControlledOverlay } from "../overlays/index.js";
import { ColumnMenu, type IColumnMenuData } from "./ColumnMenu.js";
import ColumnSelectionDialog from "./ColumnSelectionDialog.js";
import DataContextIndicator from "./DataContextIndicator.js";
import DataTable from "./DataTable.js";
import { type IGridContextProps } from "./InMemoryTableWithSelectionModel.js";
import TableColumnsSortDialog from "./TableColumnsSortDialog.js";

export interface IDataTableContainerOwnProps {
  tableSpec: InputTableQuerySpec; // for identifying the table data slice ; together with all restrictions set by the parent components
  onFeatureFocused?: (rowData: {}) => void;
  viewId?: number;
  hideCheckboxes?: boolean;
  gridContext?: IGridContextProps; // hand over to the Ag-grid component
  inactive?: boolean; // in order to let the Portals be created again
  cellRendererParamsLookup?: IStringAnyMap; // in order to provide to the cell some additional data
  tableInitializationOptions?: ITableInitializationOptions;
  tableInitializationCallback?: () => void;
  openLinkExternal?: boolean;
  initialCount?: number; // to inform the user if the initial saved group had more or less items (e.g. applied constraints like ref genome), see SEL6-2034
  portalContext?: string;
  preventReloadOfSavedView?: boolean; // when fetching saved views we have cases where the view should be loaded again and some others where it should not (e.g. features tables of the genome browser)
  onCellMouseOver?: (id: number, tableQuerySpec: InputTableQuerySpec) => void;
  onCellMouseOut?: (id: number, tableQuerySpec: InputTableQuerySpec) => void;
  noRowsText?: string;
  onSelectionModelChanged?: (selectionModel: ISelectionModel) => void;
  showButtonLabel?: boolean;
}

interface IDataTableContainerAppStateProps {
  // Redux injected props
  tableData: ITableData;
  doNotShowPerfGuidance: boolean;
  wrapColumnHeaders: boolean;
  sequenceFeatureTypes: SequenceFeatureTypes;
}

interface IDataTableContainerDispatchProps {
  loadTablePage?: (
    tableSpec: InputTableQuerySpec,
    tableInitializationOptions?: ITableInitializationOptions,
  ) => Promise<any>;
  loadTableRows?: (firstRowIndex: number, lastRowIndex: number) => Promise<any>;
  refreshTableColumns: (tableSpec: InputTableQuerySpec) => Promise<any>;
  selectTableRow?: (rowId: number, rowIndex: number, selected: boolean) => void;
  selectTableRange?: (rowIndex: number) => void;
  toggleSelection?: (selected: boolean) => void;
  sortByTableColumn?: (columnField: string, sortOrder: string, multiSort: boolean) => void;
  removeFilterForColumn?: (columnField: string) => void;
  sortByMultipleTableColumns?: (sortedColumns: ITableColumn[]) => void;
  filterByTableColumn?: (columnField: string, filterExpression: string) => void;
  setColumnList?: (orderedColumns: ITableColumn[]) => Promise<boolean>;
  resetAllFilters?: () => void;
  onHideColumn?: (column: ITableColumn, hidden: boolean) => void;
  fetchPosition?: (targetId: number, tableSpec: InputTableQuerySpec) => Promise<number>;
  loadTableView?: (viewId: number, preventReloadOfSavedView: boolean) => Promise<View>;
  destroyActiveTable: (tableSec: InputTableQuerySpec) => void;
  clearTableDataSlice: (tableSec: InputTableQuerySpec) => void;
  updateTableGuidanceState?: (doNotShowPerfGuidance: boolean) => void;
  updateUserParams?: (userParams: InputUserParamsInput) => void;
  ensureRowIdIsVisible?: (rowId: number, rowIndex: number) => void;
}

export interface IDataTableContainerProps
  extends IDataTableContainerAppStateProps,
    IDataTableContainerDispatchProps,
    IDataTableContainerOwnProps {}

interface IDataTableContainerState {
  columnMenuData?: IColumnMenuData;
  showSortDialog?: boolean;
  showColumnSelectionDialog?: boolean;
  openFilterMenuUpwards?: boolean; // automatically opens the column menu upwards if there is not enough space available to open in downwards (aka responsive)
  showGuidanceMessage?: boolean;
  error?: string;
}

function mapStateToProps(state: IAppState, ownProps: IDataTableContainerOwnProps): IDataTableContainerAppStateProps {
  return {
    tableData: state.tableData.tables[getTableDataKey(ownProps.tableSpec)],
    doNotShowPerfGuidance: state.tableData.doNotShowPerfGuidance,
    wrapColumnHeaders: state.auth.session && state.auth.session.params.wrapColumnHeaders,
    sequenceFeatureTypes: state.listing.sequenceFeatureTypes,
  };
}

function mapDispatchToProps(dispatch, ownProps: IDataTableContainerProps): IDataTableContainerDispatchProps {
  return {
    loadTablePage: (tableSpec: InputTableQuerySpec, tableInitializationOptions?: ITableInitializationOptions) =>
      dispatch(loadTable(tableSpec, tableInitializationOptions)),
    loadTableView: (viewId: number, preventReloadOfSavedView: boolean) =>
      dispatch(loadView(viewId, preventReloadOfSavedView)),
    loadTableRows: (firstRowIndex: number, lastRowIndex: number) =>
      dispatch(loadTableRows(firstRowIndex, lastRowIndex, ownProps.tableSpec)),
    refreshTableColumns: (tableSpec: InputTableQuerySpec) => dispatch(loadTableColumns(tableSpec, true)),
    selectTableRow: (rowId: number, rowIndex: number, selected: boolean) =>
      dispatch(selectTableRow(rowId, rowIndex, selected, ownProps.tableSpec, true)),
    selectTableRange: (rowIndex: number) => dispatch(selectTableRange(rowIndex, ownProps.tableSpec)),
    toggleSelection: (selected: boolean) => dispatch(toggleSelection(selected, ownProps.tableSpec)),
    sortByTableColumn: (columnField: string, sortOrder: string, multiSort: boolean) => {
      dispatch(sortByTableColumn(columnField, sortOrder, multiSort, ownProps.tableSpec));
    },
    removeFilterForColumn: (columnField: string) => {
      dispatch(filterByTableColumn({ columnField, tableSpec: ownProps.tableSpec, filterExpression: null }));
    },
    sortByMultipleTableColumns: (sortedColumns: ITableColumn[]) => {
      dispatch(sortByMultipleTableColumns(sortedColumns, ownProps.tableSpec));
    },
    filterByTableColumn: (columnField: string, filterExpression: string) =>
      dispatch(
        filterByTableColumn({
          columnField,
          filterExpression,
          tableSpec: ownProps.tableSpec,
        }),
      ),
    setColumnList: (orderedColumns: ITableColumn[]) =>
      dispatch(setOrderedColumnList(orderedColumns, ownProps.tableSpec)),
    resetAllFilters: () => dispatch(resetAllFiltersAndReloadTable(ownProps.tableSpec)),
    onHideColumn: (column: ITableColumn, hidden: boolean) =>
      dispatch(setColumnsVisibility([column], hidden, ownProps.tableSpec)),
    fetchPosition: (targetId: number, tableSpec: InputTableQuerySpec) =>
      dispatch(fetchRowPosition(targetId, tableSpec)),
    destroyActiveTable: (tableSpec: InputTableQuerySpec) => dispatch(destroyActiveTable(tableSpec)),
    clearTableDataSlice: (tableSpec: InputTableQuerySpec) => dispatch(clearTableDataSlice(tableSpec)),
    updateTableGuidanceState: (doNotShowPerfGuidance: boolean) =>
      dispatch(updateTableGuidanceState(doNotShowPerfGuidance)),
    updateUserParams: (userParams: InputUserParamsInput) => dispatch(updateUserParams(userParams)),
    ensureRowIdIsVisible: (rowId: number, rowIndex: number) =>
      dispatch(ensureRowIsVisible(ownProps.tableSpec, rowId, rowIndex)),
  };
}

export class DataTableContainerImpl extends PureComponent<IDataTableContainerProps, IDataTableContainerState> {
  // for storing statically a reference to each of the DataTable component, so that we can imperatively ask ag-grid to refresh the rows
  private static dataTableRefs: Record<string, any> = []; // map by tableSpecUniqueKey

  constructor(props: IDataTableContainerProps) {
    super(props);

    this.state = {
      columnMenuData: null,
      showSortDialog: false,
      showColumnSelectionDialog: false,
      showGuidanceMessage: false,
    };

    this.onMenuButtonClicked = this.onMenuButtonClicked.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
    this.handleFilterClicked = this.handleFilterClicked.bind(this);
    this.handleRemoveFilter = this.handleRemoveFilter.bind(this);
    this.onColumnDragStopped = this.onColumnDragStopped.bind(this);

    this.onAdvancedSortOpen = this.onAdvancedSortOpen.bind(this);
    this.onAdvancedSortCancel = this.onAdvancedSortCancel.bind(this);
    this.onAdvancedSortApply = this.onAdvancedSortApply.bind(this);
    this.calculateNumRowsSelected = this.calculateNumRowsSelected.bind(this);

    this.onColumnSelectionOpen = this.onColumnSelectionOpen.bind(this);
    this.onColumnSelectionCancel = this.onColumnSelectionCancel.bind(this);
    this.onColumnSelectionApply = this.onColumnSelectionApply.bind(this);

    this.hideColumn = this.hideColumn.bind(this);
  }

  componentDidMount() {
    this.initiateTableLoading();
  }

  componentWillUnmount() {
    this.props.destroyActiveTable(this.props.tableSpec);
    this.props.clearTableDataSlice(this.props.tableSpec);
  }

  componentDidUpdate(prevProps: IDataTableContainerProps) {
    if (prevProps.tableData !== this.props.tableData && this.props.tableData && !this.props.tableData.isLoading) {
      this.closeMenu();
    }
    if (this.props.tableData && prevProps.tableData?.selectionModel !== this.props.tableData.selectionModel) {
      this.props.onSelectionModelChanged?.(this.props.tableData.selectionModel);
    }
  }

  // getter and setter for storing/fetching statically a reference to each of the DataTable components, so that we can imperatively ask ag-grid to refresh the rows
  // not in use any longer since we do not need to imperatively ask a table to refresh (perhaps useful in the future)
  public static getDataTableRef = (tableSpec: InputTableQuerySpec) => {
    return DataTableContainerImpl.dataTableRefs[getTableDataKey(tableSpec)];
  };

  storeDataTableReference = (ref: any) => {
    DataTableContainerImpl.dataTableRefs[getTableDataKey(this.props.tableSpec)] = ref;
  };

  initiateTableLoading = () => {
    this.props.loadTableView(this.props.viewId, this.props.preventReloadOfSavedView).then((view: View) => {
      if (!view || !savedViewAppliesToTableQuerySpec(view, this.props.tableSpec)) {
        // case where the current viewId does not correspond to a table view ...
        // or is coming from another table view
        return this.props
          .loadTablePage(this.props.tableSpec, this.props.tableInitializationOptions)
          .then(() => {
            this.props.tableInitializationCallback && this.props.tableInitializationCallback();
          })
          .catch((error) => {
            this.setState({ error: error.message });
          });
      }
    });
  };

  closeMenu(
    callback: () => any = () => {
      /* empty */
    },
  ) {
    this.setState({ columnMenuData: null }, callback);
  }

  handleFilterClicked(columnField: string, filter: RsqlFilterConstraint) {
    this.closeMenu(() => this.props.filterByTableColumn(columnField, encodeFilter(filter)));
  }

  handleRemoveFilter(columnField: string) {
    this.closeMenu(() => this.props.removeFilterForColumn(columnField));
  }

  openColumnSelection() {
    this.setState({ showColumnSelectionDialog: true });
  }

  closeColumnSelection(callback: () => any = null) {
    this.setState({ showColumnSelectionDialog: false }, callback);
  }

  openAdvancedSort() {
    this.setState({ showSortDialog: true });
  }

  closeAdvancedSort(callback: () => any = null) {
    this.setState({ showSortDialog: false }, callback);
  }

  hideColumn(column: ITableColumn, hidden: boolean) {
    this.closeMenu(() => this.props.onHideColumn(column, hidden));
  }

  onAdvancedSortOpen() {
    this.closeMenu(() => this.openAdvancedSort());
  }

  onAdvancedSortCancel() {
    this.closeAdvancedSort();
  }

  onAdvancedSortApply(sortedColumns: ITableColumn[]) {
    this.closeAdvancedSort(() => this.props.sortByMultipleTableColumns(sortedColumns));
  }

  onColumnSelectionOpen(_activeColumn: ITableColumn) {
    this.closeMenu(() => this.openColumnSelection());
  }

  onColumnSelectionCancel() {
    this.closeColumnSelection();
  }

  onColumnSelectionApply(columns: ITableColumn[]) {
    return this.props.setColumnList(columns).then((done: boolean) => {
      this.closeColumnSelection();
      return done;
    });
  }

  onColumnDragStopped(orderedColumns: ITableColumn[]) {
    this.props.setColumnList(orderedColumns);
  }

  calculateNumRowsSelected() {
    return this.props.tableData.selectionModel.selectedIds.length;
  }

  onMenuButtonClicked(columnMenuData: any) {
    this.setState({ columnMenuData: columnMenuData });
  }

  onGridSizeChange = (event: GridSizeChangedEvent) => {
    const openFilterMenuUpwards: boolean = event.clientHeight < COLUMN_MENU_UPWARDS_HEIGHT_THRESHOLD;
    this.setState({ openFilterMenuUpwards });
  };

  onToggleColumnHeadersWrapping = (wrapColumnHeaders: boolean) => {
    const params: InputUserParamsInput = { wrapColumnHeaders: wrapColumnHeaders };
    this.closeMenu(() => this.props.updateUserParams(params));
  };

  private showGuidanceMessageTimer: any;

  refreshTableColumnsMetadata = () => {
    return this.props.refreshTableColumns(this.props.tableSpec);
  };

  loadTableRows = (firstRowIndex: number, lastRowIndex: number) => {
    clearTimeout(this.showGuidanceMessageTimer); // for fast consecutive requests
    this.showGuidanceMessageTimer =
      !this.props.doNotShowPerfGuidance &&
      !this.state.showGuidanceMessage &&
      showGuidanceMessageForEntity(this.props.tableSpec) &&
      setTimeout(() => this.setState({ showGuidanceMessage: true }), COLUMN_SELECTION_GUIDANCE_TIMER);

    return this.props.loadTableRows(firstRowIndex, lastRowIndex).then((response) => {
      clearTimeout(this.showGuidanceMessageTimer);
      return response;
    });
  };

  buildColumnSelectionButtonWithGuidance = () => {
    const portalContext: string = this.props.portalContext || createUniqueKeyFromTableSpec(this.props.tableSpec);
    const columnsSelectionButtonTarget = document.querySelectorAll(
      `#${COLUMN_SELECTION_BUTTON_PLACEHOLDER_ID + portalContext}`,
    );

    if (columnsSelectionButtonTarget.length === 0) return null;

    const columnsSelectionButton = (
      <PopoverController
        overlayOptions={{
          placement: "left",
          isOpen: !this.props.doNotShowPerfGuidance && this.state.showGuidanceMessage,
          onClose: () => this.props.updateTableGuidanceState(true),
        }}
        actionWidget={(controllerProps) => (
          <TriggerButton
            {...controllerProps}
            icon={"view_column"}
            label={"Columns"}
            key={"column-selection-popover"}
            isDisabled={this.showLoadingSpinner()}
            tooltip={"Modify table columns"}
            showLabel={this.props.showButtonLabel ?? true}
            onPress={() => {
              this.setState({ showColumnSelectionDialog: true });
              controllerProps.onPress?.();
            }}
          />
        )}
      >
        {(controllerProps) => (
          <Dialog {...controllerProps}>
            <div style={{ maxWidth: 350, padding: 5, fontSize: "smaller" }}>
              Please consider adjusting the visible columns or use default column template in order to reduce the data
              loading time.{" "}
              <a role="button" onClick={() => this.props.updateTableGuidanceState(true)}>
                Dismiss.
              </a>
            </div>
          </Dialog>
        )}
      </PopoverController>
    );

    const retValues = [];

    columnsSelectionButtonTarget.forEach((target) => {
      retValues.push(ReactDOM.createPortal(columnsSelectionButton, target));
    });

    return retValues;
  };

  showLoadingSpinner = () => {
    const tableData: ITableData = this.props.tableData;
    return (
      !(tableData && tableData.columns && tableData.columns.length > 0 && !tableData.isLoadingTableMeta) &&
      !this.state.error
    );
  };

  render(): ReactElement {
    const tableData: ITableData = this.props.tableData;
    const removeFilterForColumn = this.props.removeFilterForColumn;
    return (
      <div className="fill">
        {/* we always want the ProgressBar to appear even when the spinner is showing */}
        <LoadingBar
          isLoading={!tableData || tableData.isLoading || tableData.isLoadingTableMeta}
          style={{ zIndex: 2, borderRadius: 0 }}
        />
        {!this.props.inactive && this.buildColumnSelectionButtonWithGuidance()}
        {
          // let's load the grid only when the table init request has completed and columns are available ; it is possible that the columns are already
          // there but the init request has not completed (e.g. Genome Browser case where we pre-load the list of columns for having the filters available or so ...)
          // therefore we also need to make sure that the isLoadingTableMeta is over
        }
        {this.showLoadingSpinner() ? (
          <LoadingSpinner key="data-table-spinner" inCenter />
        ) : this.state.error ? (
          <ContentPlaceholder>{this.state.error}</ContentPlaceholder>
        ) : (
          <div className="fill" style={{ position: "relative" }}>
            <DataTable
              key="data-table"
              ref={this.storeDataTableReference}
              tableData={{
                ...tableData,
                tableQuerySpec: this.props.tableSpec,
              }} // because some restrictions might be applied dynamically from parent component
              onRowSelection={this.props.selectTableRow}
              onRangeSelection={this.props.selectTableRange}
              onAllSelected={this.props.toggleSelection}
              onColumnSortClick={this.props.sortByTableColumn}
              onRemoveFilterClick={removeFilterForColumn}
              loadTableRows={this.loadTableRows}
              refreshTableColumnsMetadata={this.refreshTableColumnsMetadata}
              onMenuButtonClicked={this.onMenuButtonClicked}
              onColumnDragStopped={this.onColumnDragStopped}
              onFeatureFocused={this.props.onFeatureFocused}
              fetchPosition={this.props.fetchPosition}
              hideCheckboxes={this.props.hideCheckboxes}
              noRowsText={this.props.noRowsText}
              onGridSizeChanged={this.onGridSizeChange}
              cellRendererParamsLookup={this.props.cellRendererParamsLookup}
              gridContext={{
                ...this.props.gridContext,
                expansionPath: getActiveExpansionPath(this.props.tableData),
                tableSpec: this.props.tableSpec,
              }}
              openLinkExternal={this.props.openLinkExternal}
              wrapColumnHeaders={this.props.wrapColumnHeaders}
              onToggleColumnHeadersWrapping={this.onToggleColumnHeadersWrapping}
              ensureRowIdIsVisible={this.props.ensureRowIdIsVisible}
              onCellMouseOver={this.props.onCellMouseOver}
              onCellMouseOut={this.props.onCellMouseOut}
            />

            <DataContextIndicator
              key="context-indicator"
              filterIsSet={!!tableData.tableQuerySpec.rowFilter}
              numTotalRows={tableData.restrictedRowCount}
              numRowsFiltered={tableData.filteredRowCount}
              numRowsSelected={this.calculateNumRowsSelected()}
              rowEntityLabel={getSequenceFeatureTypeDisplayName(this.props.tableSpec, this.props.sequenceFeatureTypes)}
              onResetFilter={this.props.resetAllFilters}
              numInitialRows={this.props.initialCount}
            />

            <ColumnMenu
              key="column-menu"
              columnMenuData={this.state.columnMenuData}
              tableQuerySpec={this.props.tableSpec}
              onFilter={this.handleFilterClicked}
              onRemoveFilter={this.handleRemoveFilter}
              onClose={() => this.closeMenu()}
              onAdvancedSortClick={this.onAdvancedSortOpen}
              onColumnSelectionClick={this.onColumnSelectionOpen}
              onHideColumn={this.hideColumn}
              onToggleColumnHeadersWrapping={this.onToggleColumnHeadersWrapping}
              overlayColumnMenuOnTop={this.state.openFilterMenuUpwards}
              wrapColumnHeaders={this.props.wrapColumnHeaders}
            />

            {this.state.showSortDialog && (
              <ModalControlledOverlay close={this.onAdvancedSortCancel} isOpen={true}>
                {({ close }) => (
                  <TableColumnsSortDialog
                    key="sort-dialog"
                    tableData={tableData}
                    onCancel={close}
                    onApply={this.onAdvancedSortApply}
                  />
                )}
              </ModalControlledOverlay>
            )}

            {this.state.showColumnSelectionDialog && (
              <ColumnSelectionDialog
                key="column-selection-dialog"
                columns={tableData && tableData.columns}
                columnTemplates={tableData && tableData.columnTemplates}
                tableSpec={tableData?.tableQuerySpec}
                isLoadingTemplates={tableData && tableData.isLoadingTemplates}
                onCancel={() => {
                  this.onColumnSelectionCancel();
                  this.setState({ showColumnSelectionDialog: false });
                }}
                onApply={(columns) => this.onColumnSelectionApply(columns)}
              />
            )}
          </div>
        )}
      </div>
    );
  }
}

const DataTableContainer = connect<
  IDataTableContainerAppStateProps,
  IDataTableContainerDispatchProps,
  IDataTableContainerOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(DataTableContainerImpl);

export default DataTableContainer;
